import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import * as yup from 'yup';
import { uniq, sortBy } from 'underscore';

import t from 'react-translate';
import { useAppDispatch } from 'redux/store';

import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import NvFilePicker from 'shared/components/nv-filepicker';
import { CSVHeader, parseCSV } from 'shared/csv-utils';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import LoadingWrapper, { LoaderType } from 'shared/components/loading-wrapper';
import NvIcon from 'shared/components/nv-icon';

const templateHeaders: CSVHeader[] = [
  {
    key: 'topic',
    headerName: 'Topic',
  },
  {
    key: 'description',
    headerName: 'Description',
  },
  {
    key: 'status',
    headerName: 'Status',
  },
];

interface TopicsCSVPickerProps {
  onConfirmTopics: (topics: string[]) => void;
}

const TopicsCSVPicker: React.FC<TopicsCSVPickerProps> = ({ children, onConfirmTopics }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [topics, setTopics] = useState<string[]>([]);
  const [showTopicsModal, setShowTopicsModal] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const validationSchema = yup.object().shape({
    topic: yup.string()
      .required(t.VALIDATION.REQUIRED()),
  });

  const parseCallback = (response) => {
    setLoading(false);

    if (response.errors.length > 0 || response.data.length === 0) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.SOMETHING_WRONG(),
        message: t.FORM.ERROR_TRY_AGAIN(),
      }));
      setShowTopicsModal(false);
    } else {
      const uploadedTopics = sortBy(
        uniq(topics.concat(response.data.map((row) => row.topic))),
        (topic) => topic.toLowerCase(),
      );
      setTopics(uploadedTopics);
    }
  };

  const onCSVSelected = (files: File[]) => {
    setShowTopicsModal(true);
    const [file] = files;

    if (!file) {
      return;
    }
    setLoading(true);

    parseCSV({
      headers: templateHeaders,
      file,
      validationSchema,
      callback: parseCallback,
    });
  };

  const onConfirm = () => {
    setShowTopicsModal(false);
    onConfirmTopics(topics);
  };

  return (
    <React.Fragment>
      <NvFilePicker
        multiple={false}
        accept={['.csv', 'text/csv']}
        onChange={(e) => onCSVSelected(e)}
      >
        {children}
      </NvFilePicker>
      {showTopicsModal && (
        <NvModal
          show
          width={720}
          height='unset'
          type={ModalType.FIXED}
          header={t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.ADD_TOPICS()}
          doubleModal
          body={(
            <LoadingWrapper
              isLoaded={!loading}
              loaderType={LoaderType.PLACEHOLDER}
              keepRendered={false}
              className='p-5'
              loadingText={t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.PROCESSING()}
            >
              <div className='d-flex flex-column align-items-center p-4'>
                <div className='align-self-start w-100'>
                  <div className='label gray-2 mb-2'>
                    {t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.FOUND()}
                  </div>
                  {topics.map((topic, index) => (
                    <div
                      className={`border-bottom border-gray-5 text-regular font-weight-bolder my-0 py-2 pl-2 ${index === 0 ? 'border-top' : ''}`}
                      key={topic}
                    >
                      {topic}
                    </div>
                  ))}
                </div>

                <div className='d-flex justify-content-center text-regular mt-4'>
                  <NvFilePicker
                    multiple={false}
                    accept={['.csv', 'text/csv']}
                    onChange={(e) => onCSVSelected(e)}
                  >
                    <div
                      className='d-flex text-primary border border-primary justify-content-center align-items-center px-3 py-1 rounded cursor-pointer font-weight-bold'
                    >
                      <NvIcon
                        icon='upload'
                        size='smallest'
                        className='mr-2 text-primary'
                      />
                      <span>
                        {t.FORM.UPLOAD_NEW_CSV()}
                      </span>
                    </div>
                  </NvFilePicker>
                  <Button
                    variant='primary'
                    className='ml-2 px-3 py-1'
                    onClick={onConfirm}
                  >
                    {t.FORM.CONFIRM()}
                  </Button>
                </div>
              </div>
            </LoadingWrapper>
            )}
          onClose={() => {
            setShowTopicsModal(false);
            setTopics([]);
          }}
        />
      )}
    </React.Fragment>
  );
};

export default TopicsCSVPicker;
